import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Users } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";

export default function AccessSettingsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ownOnly, setOwnOnly] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/settings");
        setOwnOnly((data?.counsellor_lead_scope || "all") === "own");
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onToggle = async (next) => {
    setOwnOnly(next);
    setSaving(true);
    try {
      await api.put("/admin/access-settings", {
        counsellor_lead_scope: next ? "own" : "all",
      });
      toast.success(
        next
          ? "Counsellors now see only their own claimed leads"
          : "Counsellors can see all tenant leads",
      );
    } catch (e) {
      setOwnOnly(!next); // revert on failure
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-slate-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="access-settings-tab">
      <div className="flex items-start justify-between gap-4 rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#1877F2]/10 text-[#1877F2]">
            <Users className="h-5 w-5" />
          </span>
          <div>
            <div className="text-sm font-semibold text-[#0A2540]">
              Counsellors see only their own leads
            </div>
            <p className="mt-1 max-w-xl text-xs text-slate-500">
              When ON, each counsellor sees only the leads assigned to them
              <span className="font-medium text-[#0A2540]"> plus unassigned/new leads</span> they
              can still claim — other counsellors' claimed leads stay hidden. Admins and managers
              always see everything. When OFF, all counsellors share full lead visibility.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 pt-1">
          {saving && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          <Switch
            checked={ownOnly}
            onCheckedChange={onToggle}
            disabled={saving}
            data-testid="counsellor-own-leads-switch"
          />
        </div>
      </div>
    </div>
  );
}
